import { templateIdForServer } from '@electron/shared/mcp-template-gating';
import {
  OUTLOOK_TEMPLATE_ID,
  type OutlookSignInState,
} from '@electron/shared/outlook-sign-in.types';
import { useEffect, useRef, useState } from 'react';

import { electronApis } from '@/features/electron-api';
import { useMcpServersQuery } from '@/features/settings/sub-settings/settings-local-mcp.query';
import { logger } from '@/lib/logger';

const POLL_MS = 3_000;
// A crashed server restarts on the next call, so one failed check is not final
const MAX_FAILED_CHECKS = 3;

const inlineSignInSlugs = new Set<string>();

export type OutlookSignIn =
  | Exclude<OutlookSignInState, { state: 'pending' }>
  | { state: 'failed' };

/** True while a Connect dialog shows this server's sign-in code itself */
export const isSignInShownInline = (slug: string): boolean =>
  inlineSignInSlugs.has(slug);

/**
 * Once Outlook is saved, starts its Microsoft sign-in and polls until it
 * finishes. `signIn` is the latest code or signed-in account, `null` until the
 * server answers with either, and `failed` once checks keep failing; `retry`
 * starts polling again.
 */
export function useOutlookSignIn(connected: boolean): {
  signIn: OutlookSignIn | null;
  retry: () => void;
} {
  const servers = useMcpServersQuery().data;
  const slug = connected
    ? servers?.find(
        (entry) => templateIdForServer(entry) === OUTLOOK_TEMPLATE_ID
      )?.slug
    : undefined;
  const [signIn, setSignIn] = useState<OutlookSignIn | null>(null);
  const [round, setRound] = useState(0);
  const signedInRef = useRef(false);

  // Closing before sign-in finishes would leave the server waiting on a code
  // nobody sees, so a later chat call gets no new code; restarting it clears that
  useEffect(() => {
    if (!slug) return;
    return () => {
      if (signedInRef.current) return;
      electronApis.axonMcp.respawnServer(slug).catch((error: unknown) => {
        logger.warn('Resetting the unfinished Outlook sign-in failed', {
          error,
        });
      });
    };
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    inlineSignInSlugs.add(slug);
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let failedChecks = 0;

    const check = async () => {
      let next: OutlookSignInState;
      try {
        next = await electronApis.axonMcp.checkOutlookSignIn(slug);
        failedChecks = 0;
      } catch (error) {
        if (stopped) return;
        failedChecks += 1;
        if (failedChecks >= MAX_FAILED_CHECKS) {
          logger.error('Checking the Outlook sign-in kept failing', { error });
          setSignIn({ state: 'failed' });
          return;
        }
        logger.warn('Checking the Outlook sign-in failed', { error });
        next = { state: 'pending' };
      }
      if (stopped) return;
      if (next.state === 'signed_in') signedInRef.current = true;
      if (next.state !== 'pending') setSignIn(next);
      if (next.state !== 'signed_in')
        timer = setTimeout(() => void check(), POLL_MS);
    };
    void check();

    return () => {
      stopped = true;
      clearTimeout(timer);
      inlineSignInSlugs.delete(slug);
    };
  }, [slug, round]);

  const retry = () => {
    setSignIn(null);
    setRound((current) => current + 1);
  };

  return { signIn, retry };
}
